import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams } from "react-router";
import { usePrefixedNavigate } from '@edusense/shared-hooks';
import { useAuth } from '@edusense/shared-auth-core';
import { isLearnSurface } from '@edusense/shared-constants';
import { useGetProfileByUsername } from '@edusense/user-client';
import { useTweets, useProfileHandlers, useScreenHeight, useMobileView, useProfileMetaStyles } from '../hooks';
import { useProfileContent } from '@edusense/user-profile';
import { ProfileProps, ProfileTabType } from '../base';

// 컴포넌트 import
import { ProfileBannerSection } from '@edusense/features-user-profile';
import { ProfileHeader, ProfileTabs, ProfileContent } from '../components';
import { ProfilePageWrapper, ProfileContentContainer } from '../Profile.styles';
import { StatusDisplay } from '@edusense/shared-ui-kit';
import { 
  profile_statusLoading,
  profile_statusError,
  profile_statusNotFound,
  common_error
} from '@paraglide/messages';
import { PROFILE_PAGE_TEST_IDS } from '@edusense/shared-constants';

// 상수
const PAGE_SIZE = 12;

const extractProfileData = (profile: any) => {
  if (!profile) {
    return undefined;
  }

  if (profile?.basicInfo || profile?.profileInfo || profile?.auth) {
    return profile;
  }

  if ('data' in profile && profile.data) {
    return profile.data;
  }

  return profile;
};

const ensureProfileResponseShape = (profile: any) => {
  if (!profile) {
    return undefined;
  }

  if ('data' in profile && profile.data) {
    return profile;
  }

  return { data: profile };
};

// 로그 메시지 상수
const LOG_MESSAGES = {
  PROFILE_AUTH_REQUIRED: '[Profile] 자신의 프로필 접근 시 로그인 필요 - 랜딩 페이지로 이동',
  PROFILE_OTHER_USER_ACCESS: '[Profile] 다른 사용자 프로필 접근 - 로그인 불필요 (username)',
};

// CSS 클래스 상수
const CSS_CLASSES = {
  PROFILE_CONTENT_CONTAINER: 'profile-content-container',
};

const Profile: React.FC<ProfileProps> = ({
  userId: propUserId,
  profile: propProfile,
  isAuthenticated: propIsAuthenticated,
  onGoBack,
  onTabChange,
  initialTab = isLearnSurface() ? 'teaching' : 'tweets',
  showToast
}) => {
  // URL에서 username 추출
  const { username: urlUsername } = useParams<{ username: string }>();
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const navigate = usePrefixedNavigate();
  
  // 프로필 식별자 결정: URL의 username 우선, 그 다음 현재 사용자의 username
  const finalUsername = urlUsername || user?.username;

  console.log('[Profile] Username 결정 로직:', {
    urlUsername,
    userUsername: user?.username,
    finalUsername,
    userId: user?.id
  });
  

  // 상태 관리
  const [activeTab, setActiveTab] = useState<ProfileTabType>(initialTab);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [replyToTweet, setReplyToTweet] = useState<any>(null);

  // 인증 체크 - 자신의 프로필을 보려는 경우에만 로그인 필요
  useEffect(() => {
    // 🛡️ 인증 상태가 아직 로딩 중이면 리다이렉트하지 않음 (새로고침 시 중요)
    if (isAuthLoading) {
      console.log('[Profile] 인증 상태 로딩 중 - 리다이렉트 대기');
      return;
    }
    
    // URL에 username이 없으면 자신의 프로필을 보려는 것이므로 로그인 필요
    const isViewingOwnProfile = !urlUsername;
    
    console.log('[Profile] 인증 상태 체크:', {
      isViewingOwnProfile,
      isAuthenticated,
      isAuthLoading,
      urlUsername,
      willRedirect: isViewingOwnProfile && !isAuthenticated
    });
    
    if (isViewingOwnProfile && !isAuthenticated) {
      console.log(LOG_MESSAGES.PROFILE_AUTH_REQUIRED);
      navigate(isLearnSurface() ? '/learn' : '/landing');
    } else if (urlUsername) {
      console.log(LOG_MESSAGES.PROFILE_OTHER_USER_ACCESS, { urlUsername });
    }
  }, [isAuthenticated, isAuthLoading, urlUsername, navigate]);

  // Hooks - username으로만 프로필 조회 (비 v1 경로 단일화)
  const { data: fetchedProfileResponse, isLoading: profileLoading, error, refetch: refreshProfile } = useGetProfileByUsername(
    finalUsername || '', 
    { 
      query: { 
        enabled: !!finalUsername,
        staleTime: 30 * 1000, // 30초 후 데이터를 stale로 간주
        gcTime: 5 * 60 * 1000, // 5분간 캐시 유지 (React Query v5: cacheTime renamed to gcTime)
        refetchInterval: 60 * 1000, // 60초마다 자동 refetch
        refetchIntervalInBackground: true, // 백그라운드에서도 refetch
        refetchOnWindowFocus: true // 윈도우 포커스 시 refetch
      } 
    }
  );
  console.log('[Profile] useGetProfileByUsername 결과:', { 
    finalUsername,
    hasResponse: !!fetchedProfileResponse,
    responseKeys: fetchedProfileResponse ? Object.keys(fetchedProfileResponse) : [],
    profileLoading, 
    error 
  });

  const normalizedFetchedProfile = useMemo(() => extractProfileData(fetchedProfileResponse), [fetchedProfileResponse]);
  const normalizedPropProfile = useMemo(() => extractProfileData(propProfile), [propProfile]);
  const normalizedProfile = normalizedFetchedProfile ?? normalizedPropProfile;

  const profileOwnerIdFromProfile = normalizedProfile?.basicInfo?.id
    ?? normalizedProfile?.userId
    ?? normalizedProfile?.id;

  const profileOwnerId = profileOwnerIdFromProfile != null
    ? String(profileOwnerIdFromProfile)
    : (propUserId != null ? String(propUserId) : undefined);

  const headerProfileSource = useMemo(() => {
    if (fetchedProfileResponse) {
      return fetchedProfileResponse;
    }

    if (propProfile) {
      return ensureProfileResponseShape(propProfile);
    }

    if (normalizedProfile) {
      return ensureProfileResponseShape(normalizedProfile);
    }

    return undefined;
  }, [fetchedProfileResponse, propProfile, normalizedProfile]);

  // v1 경로는 사용하지 않음 (서버/게이트웨이 규약에 따라 비 v1로 단일화)

  const fallbackUserId = user?.id != null ? String(user.id) : undefined;
  const tweetUserId = profileOwnerId ?? fallbackUserId;

  const { tweets, loading: tweetsLoading, refreshTweets } = useTweets(tweetUserId);

  // 프로필 컨텐츠 관리 훅
  const {
    userProjects, setUserProjects, projectsPage, setProjectsPage, hasMoreProjects, setHasMoreProjects, projectsLoading,
    userCourses, setUserCourses, coursesPage, setCoursesPage, hasMoreCourses, setHasMoreCourses, coursesLoading,
    userRoadmaps, setUserRoadmaps, roadmapsPage, setRoadmapsPage, hasMoreRoadmaps, setHasMoreRoadmaps, roadmapsLoading,
    fetchUserProjects, fetchUserCourses, fetchUserRoadmaps
  } = useProfileContent({
    userId: profileOwnerId,
    showToast
  });

  // Responsive hooks
  const { screenCompactMode } = useScreenHeight();
  const isMobileView = useMobileView();
  const profileMetaRef = useRef<HTMLDivElement>(null);

  // 동적 스타일 적용
  useProfileMetaStyles(isMobileView, screenCompactMode, profileMetaRef);

  // 최종 인증 상태
  const finalIsAuthenticated = propIsAuthenticated ?? isAuthenticated;

  // 핸들러들
  const profileHandlers = useProfileHandlers({
    profile: normalizedProfile,
    activeTab,
    isAuthenticated: finalIsAuthenticated,
    refreshTweets,
    showToast,
    onTabChange,
    setActiveTab,
    setShowComposeModal,
    setReplyToTweet,
    tweets,
    userProjects,
    setUserProjects,
    setProjectsPage,
    setHasMoreProjects,
    fetchUserProjects,
    userCourses,
    setUserCourses,
    setCoursesPage,
    setHasMoreCourses,
    fetchUserCourses,
    userRoadmaps,
    setUserRoadmaps,
    setRoadmapsPage,
    setHasMoreRoadmaps,
    fetchUserRoadmaps
  });

  useEffect(() => {
    const targetUserId = profileOwnerId;
    if (!targetUserId) {
      return;
    }

    if (activeTab === 'learning' && userCourses.length === 0 && !coursesLoading && hasMoreCourses) {
      setCoursesPage(0);
      setHasMoreCourses(true);
      void fetchUserCourses(targetUserId, 0, PAGE_SIZE);
    }

    if (activeTab === 'roadmaps' && userRoadmaps.length === 0 && !roadmapsLoading && hasMoreRoadmaps) {
      setRoadmapsPage(0);
      setHasMoreRoadmaps(true);
      void fetchUserRoadmaps(targetUserId, 0, PAGE_SIZE);
    }
  }, [
    activeTab,
    profileOwnerId,
    userCourses.length,
    coursesLoading,
    fetchUserCourses,
    setCoursesPage,
    setHasMoreCourses,
    hasMoreCourses,
    userRoadmaps.length,
    roadmapsLoading,
    fetchUserRoadmaps,
    setRoadmapsPage,
    setHasMoreRoadmaps,
    hasMoreRoadmaps
  ]);

  // Load More 핸들러들
  const handleLoadMoreProjects = () => {
    const targetUserId = profileOwnerId;
    if (targetUserId && hasMoreProjects && !projectsLoading) {
      const nextPage = projectsPage + 1;
      setProjectsPage(nextPage);
      fetchUserProjects(String(targetUserId), nextPage, PAGE_SIZE);
    }
  };

  const handleLoadMoreCourses = () => {
    const targetUserId = profileOwnerId;
    if (targetUserId && hasMoreCourses && !coursesLoading) {
      const nextPage = coursesPage + 1;
      setCoursesPage(nextPage);
      fetchUserCourses(String(targetUserId), nextPage, PAGE_SIZE);
    }
  };

  const handleLoadMoreRoadmaps = () => {
    const targetUserId = profileOwnerId;
    if (targetUserId && hasMoreRoadmaps && !roadmapsLoading) {
      const nextPage = roadmapsPage + 1;
      setRoadmapsPage(nextPage);
      fetchUserRoadmaps(String(targetUserId), nextPage, PAGE_SIZE);
    }
  };

  const profileForHeader = headerProfileSource;
  const profileForContent = normalizedProfile ?? extractProfileData(headerProfileSource);
  const profileBasicInfo = profileForContent?.basicInfo;
  const profileUsername = profileBasicInfo?.username ?? finalUsername;

  const authFlagIsCurrentUser = profileForContent?.auth?.isCurrentUser === true
    || normalizedProfile?.auth?.isCurrentUser === true
    || normalizedPropProfile?.auth?.isCurrentUser === true;

  const isProfileOwnerById = profileOwnerIdFromProfile != null
    && user?.id != null
    && String(user.id) === String(profileOwnerIdFromProfile);

  const isProfileOwnerByUsername = Boolean(
    user?.username
    && profileUsername
    && user.username.toLowerCase() === String(profileUsername).toLowerCase()
  );

  const isProfileOwner = authFlagIsCurrentUser || isProfileOwnerById || isProfileOwnerByUsername;
  const hasProfile = !!profileForHeader || !!profileForContent;

  // 항상 Wrapper를 렌더링하고, 내부에서 상태별 콘텐츠를 전환
  return (
    <ProfilePageWrapper data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_PAGE_WRAPPER}>
      {/* ProfileBannerSection은 로딩이 완료되고 프로필이 있을 때만 렌더링 */}
      {!profileLoading && hasProfile && (
        <ProfileBannerSection
          data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_BANNER_SECTION}
          profile={profileForHeader ?? profileForContent}
          onGoBack={onGoBack}
          isMobileView={isMobileView}
          isOwnProfile={isProfileOwner}
          sessionProfileImage={isProfileOwner ? (user?.profileImage ?? null) : null}
        />
      )}

      <ProfileContentContainer
        className={CSS_CLASSES.PROFILE_CONTENT_CONTAINER}
        data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_CONTENT_CONTAINER}
      >
        {profileLoading ? (
          <StatusDisplay type="loading" message={profile_statusLoading()} />
        ) : error && !hasProfile ? (
          <StatusDisplay type="error" message={profile_statusError({ message: (error as any)?.message || common_error() })} />
        ) : !hasProfile ? (
          <StatusDisplay type="empty" message={profile_statusNotFound()} />
        ) : (
          <>
            <ProfileHeader
              data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_HEADER}
              profile={profileForHeader ?? ensureProfileResponseShape(profileForContent)}
              isAuthenticated={finalIsAuthenticated}
              refreshProfile={refreshProfile}
              showToast={showToast}
              screenCompactMode={screenCompactMode}
              isMobileView={isMobileView}
              profileMetaRef={profileMetaRef}
              usernameHint={finalUsername}
            />

            <ProfileTabs
              data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_TABS}
              activeTab={activeTab}
              onTabChange={profileHandlers.handleTabClick}
              screenCompactMode={screenCompactMode}
            />

            <ProfileContent
              data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_CONTENT}
              activeTab={activeTab}
              profile={profileForContent}
              isAuthenticated={finalIsAuthenticated}
              isProfileOwner={isProfileOwner}
              tweets={tweets}
              tweetsLoading={tweetsLoading}
              userProjects={userProjects}
              userCourses={userCourses}
              userRoadmaps={userRoadmaps}
              showComposeModal={showComposeModal}
              replyToTweet={replyToTweet}
              projectsLoading={projectsLoading}
              coursesLoading={coursesLoading}
              roadmapsLoading={roadmapsLoading}
              hasMoreProjects={hasMoreProjects}
              hasMoreCourses={hasMoreCourses}
              hasMoreRoadmaps={hasMoreRoadmaps}
              onComment={profileHandlers.handleComment}
              onTweetSubmit={profileHandlers.handleTweetSubmit}
              onCloseModal={profileHandlers.handleCloseModal}
              onLoadMoreProjects={handleLoadMoreProjects}
              onLoadMoreCourses={handleLoadMoreCourses}
              onLoadMoreRoadmaps={handleLoadMoreRoadmaps}
              handleEditProject={profileHandlers.handleEditProject}
              handleDeleteProject={profileHandlers.handleDeleteProject}
              handleDuplicateProject={profileHandlers.handleDuplicateProject}
              handleShareProject={profileHandlers.handleShareProject}
              handleReportProject={profileHandlers.handleReportProject}
              handleViewProjectDetails={profileHandlers.handleViewProjectDetails}
              handleViewRoadmapDetails={profileHandlers.handleViewRoadmapDetails}
              handleEditRoadmap={profileHandlers.handleEditRoadmap}
              handleDeleteRoadmap={profileHandlers.handleDeleteRoadmap}
              refreshTweets={refreshTweets}
            />
          </>
        )}
      </ProfileContentContainer>
    </ProfilePageWrapper>
  );
};

export default Profile;
